import React, { useState, useEffect } from "react"
import { CardDescription, CardTitle } from "@/shared/components/ui/card"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { useToast } from "@/shared/components/Toast"
import { useAuth } from "@/features/auth/stores/AuthContext"
import { User, Mail, Save, Loader2, Lock, ShieldCheck } from "lucide-react"
import { AdminPageHeader, AdminSectionCard } from "@/features/admin/components/common/layout"
import { ChangePasswordModal } from "@/shared/components/ChangePasswordModal"

const Account: React.FC = () => {
  const { showToast } = useToast()
  const { user, updateUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || ''
  })

  useEffect(() => {
    // Initialize form data from current user context
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }))
    }
  }, [user])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSaveProfile = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      showToast({
        title: "Error",
        message: "Name and email are required",
        type: "error"
      })
      return
    }

    setLoading(true)
    try {
      const sessionId = sessionStorage.getItem('session_id')
      const response = await fetch('/api/v2/users.php/profile', {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId || ''
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim()
        })
      })
      
      const data = await response.json()
      
      if (data.success && data.data) {
        // Update the user context with new data
        updateUser({
          name: data.data.name,
          email: data.data.email,
          updatedAt: data.data.updatedAt
        })
        
        showToast({
          title: "Success",
          message: "Profile updated successfully",
          type: "success"
        })
      } else {
        showToast({
          title: "Error",
          message: data.message || "Failed to update profile",
          type: "error"
        })
      }
    } catch (error) {
      console.error('Profile update error:', error)
      showToast({
        title: "Error",
        message: "Failed to update profile",
        type: "error"
      })
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChangeSuccess = () => {
    showToast({
      title: "Success",
      message: "Password changed successfully. All other sessions have been logged out for security.",
      type: "success"
    })
    setShowChangePassword(false)
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Account Settings"
        description="Manage your account information and security settings"
        icon={User}
      />

      <div className="space-y-6">
        {/* Profile Information */}
        <AdminSectionCard variant="elevated">
          <div className="p-6 pb-4 border-b border-slate-200/60">
            <CardTitle className="flex items-center gap-2 text-slate-800">
              <User className="h-5 w-5 text-primary" />
              Profile Information
            </CardTitle>
            <CardDescription className="text-slate-600 mt-1">
              Update your personal information
            </CardDescription>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700 font-semibold">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter your full name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-semibold">Email Address</Label>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="pt-4">
              <Button 
                onClick={handleSaveProfile}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Profile
                  </>
                )}
              </Button>
            </div>
          </div>
        </AdminSectionCard>

        {/* Change Password */}
        <AdminSectionCard variant="elevated">
          <div className="p-6 pb-4 border-b border-slate-200/60">
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Change Password
            </CardTitle>
            <CardDescription className="mt-1">
              Update your password to keep your account secure
            </CardDescription>
          </div>
          <div className="p-6">
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="p-2 bg-orange-100 rounded-lg">
                <ShieldCheck className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-slate-800 mb-1">
                  Two-Factor Verification Required
                </h4>
                <p className="text-sm text-slate-600 mb-4">
                  For your security, changing your password requires email verification. We'll send a code to your registered email address.
                </p>
                <Button 
                  onClick={() => setShowChangePassword(true)}
                  variant="default"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </Button>
              </div>
            </div>
          </div>
        </AdminSectionCard>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={showChangePassword}
        onOpenChange={setShowChangePassword}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  )
}

export default Account
